import { isMobile } from '@core/mobile/isMobile';
import { Toast, toaster } from '@kobalte/core/toast';
import CheckIcon from '@phosphor/check.svg';
import ExclamationIcon from '@phosphor/exclamation-mark.svg';
import Spinner from '@phosphor/spinner.svg';
import XIcon from '@phosphor/x.svg';
import { Button, cn, Surface } from '@ui';
import type { Component, JSX } from 'solid-js';
import {
  createEffect,
  createSignal,
  For,
  Match,
  on,
  onCleanup,
  onMount,
  Show,
  Switch,
} from 'solid-js';
import { Dynamic } from 'solid-js/web';

export enum ToastType {
  SUCCESS = 'success',
  FAILURE = 'failure',
  ALERT = 'alert',
  LOADING = 'loading',
}

interface ToastStyle {
  background: string;
  /** Accent color for icon and icon background */
  accent: string;
  /** Border color class for animated border (Tailwind class, e.g. 'border-success') */
  borderColor: string;
  /** Text color for title */
  titleText: string;
  /** Text color for subtext/description */
  subtitleText: string;
  /** Icon component */
  icon: Component<{ class?: string }>;
  /** Action button styles */
  button: {
    background: string;
    hover: string;
    text: string;
  };
  /** Close button hover text color */
  closeButtonHover: string;
}

const TOAST_STYLES: Record<ToastType, ToastStyle> = {
  [ToastType.SUCCESS]: {
    background: 'bg-success-bg',
    accent: 'bg-success',
    borderColor: 'var(--color-success)',
    titleText: 'text-success-ink',
    subtitleText: 'text-success-ink/70',
    icon: CheckIcon,
    button: {
      background: 'bg-success',
      hover: 'hover:bg-success/80',
      text: 'text-success-ink',
    },
    closeButtonHover: 'hover:text-success-ink hover:bg-success-hover',
  },
  [ToastType.FAILURE]: {
    background: 'bg-failure-bg',
    accent: 'bg-failure',
    titleText: 'text-failure-ink',
    borderColor: 'var(--color-failure)',
    subtitleText: 'text-failure-ink/70',
    icon: ExclamationIcon,
    button: {
      background: 'bg-failure',
      hover: 'hover:bg-failure/80',
      text: 'text-failure-ink',
    },
    closeButtonHover: 'hover:text-failure-ink hover:bg-failure-hover',
  },
  [ToastType.ALERT]: {
    background: 'bg-alert/10',
    accent: 'bg-alert',
    borderColor: 'var(--color-alert)',
    titleText: 'text-alert-ink',
    subtitleText: 'text-alert-ink/70',
    icon: ExclamationIcon,
    button: {
      background: 'bg-alert',
      hover: 'hover:bg-alert/80',
      text: 'text-alert-ink',
    },
    closeButtonHover: 'hover:text-alert-ink hover:bg-alert/10',
  },
  [ToastType.LOADING]: {
    background: 'bg-accent/10',
    accent: 'bg-accent',
    borderColor: 'var(--color-edge)',
    titleText: 'text-ink',
    subtitleText: 'text-ink-muted',
    icon: Spinner,
    button: {
      background: 'bg-accent',
      hover: 'hover:bg-accent/80',
      text: 'text-surface',
    },
    closeButtonHover: 'hover:text-accent hover:bg-accent/10',
  },
};

/** A single entry in the actions row — icon and label rendered as a button */
interface ToastAction {
  label: string;
  icon?: Component<{ class?: string }>;
  onClick: () => void;
}

/**
 * Common options for all toast calls.
 */
interface ToastOptions {
  subtext?: string;
  /** Auto-dismiss duration in ms. When omitted, the toast uses a default 3s timer. */
  duration?: number;
  /** When true, don't render this toast on mobile. */
  hideOnMobile?: boolean;
}

interface ToastSuccessOptions extends ToastOptions {
  actions?: ToastAction[];
  /** When true, bypasses the 3s duplicate-message throttle. */
  stack?: boolean;
}

/**
 * Config for a fully custom toast.
 * Replaces the icon, title, and accent color of the standard layout while
 * still using the shared Surface chrome and progress/dismiss machinery.
 */
export interface CustomToastConfig {
  title: string;
  content?: () => JSX.Element;
  icon?: Component<{ class?: string }>;
  /** Any CSS color value, e.g. 'var(--color-success)' or '#ff6600' */
  color?: string;
  actions?: ToastAction[];
}

interface ToastMessage {
  message: string;
  toastType: ToastType;
  timestamp: number;
  timeoutId: ReturnType<typeof setTimeout>;
  toastId?: number;
  subtext?: string;
  actions?: ToastAction[];
}

const recentToasts: Map<string, ToastMessage> = new Map();
const THROTTLE_DURATION = 3000;

/**
 * The currently-visible toast in the main region. Each new toast dismisses the
 * previous one immediately so transient notifications never stack.
 */
let activeToastId: number | undefined;
/**
 * The currently-visible mobile toast. The mobile region only shows one toast
 * at a time — each new mobile toast dismisses the previous one immediately.
 */
let activeMobileToastId: number | undefined;

function getActiveToastId(region: string): number | undefined {
  if (region === 'mobile-toast-region') return activeMobileToastId;
  if (region === 'toast-region') return activeToastId;
  return undefined;
}

function setActiveToastId(region: string, toastId: number | undefined): void {
  if (region === 'mobile-toast-region') {
    activeMobileToastId = toastId;
  } else if (region === 'toast-region') {
    activeToastId = toastId;
  }
}

function dismissActiveToast(region: string): boolean {
  const activeId = getActiveToastId(region);
  if (activeId === undefined) return false;

  setActiveToastId(region, undefined);
  toaster.dismiss(activeId);
  return true;
}

/**
 * Hand the region's single visible slot to a new toast, and report whether it
 * displaced one (so the newcomer can skip its entrance animation).
 *
 * Persistent toasts opt out of the slot entirely: they are prompts the user is
 * expected to answer, so a passing "Copied" must not tear one down, and a
 * prompt appearing must not swallow a result the user is still reading. They
 * stack in the region instead, and leave only when dismissed.
 */
function replaceActiveToast(region: string, persistent?: boolean): boolean {
  if (persistent) return false;
  return dismissActiveToast(region);
}

function trackActiveToast(
  region: string,
  toastId: number,
  persistent?: boolean
): void {
  if (persistent) return;
  setActiveToastId(region, toastId);
}

function clearTrackedToast(region: string, toastId: number): void {
  if (getActiveToastId(region) === toastId) {
    setActiveToastId(region, undefined);
  }
}

function createToastKey(message: string, type: ToastType): string {
  return `${type}:${message}`;
}

function dismissIfRecent(message: string, type: ToastType): void {
  const key = createToastKey(message, type);
  const existingToast = recentToasts.get(key);
  if (!existingToast) return;

  const now = Date.now();
  if (
    now - existingToast.timestamp < THROTTLE_DURATION &&
    existingToast.toastId != null
  ) {
    toaster.dismiss(existingToast.toastId);
  }
}

// Tell users that an action has successfully completed
function success(
  message: string,
  options?: ToastSuccessOptions
): number | undefined {
  if (!options?.stack) dismissIfRecent(message, ToastType.SUCCESS);
  return createToast(message, ToastType.SUCCESS, options);
}

function dismiss(toastId: number) {
  toaster.dismiss(toastId);
}

// Tell users that an action has failed, because of us
function failure(
  message: string,
  options?: ToastOptions & { actions?: ToastAction[] }
) {
  dismissIfRecent(message, ToastType.FAILURE);
  createToast(message, ToastType.FAILURE, options);
}

// Tell users that an action has failed, because of them
function alert(message: string, options?: ToastOptions) {
  dismissIfRecent(message, ToastType.ALERT);
  createToast(message, ToastType.ALERT, options);
}

function ActionButtons(props: { actions: ToastAction[]; mobile?: boolean }) {
  return (
    <For each={props.actions}>
      {(action) => (
        <Button
          size={props.mobile ? 'sm' : 'md'}
          onClick={action.onClick}
          variant="outline"
          class="px-2 py-1 bg-surface"
          depth={4}
        >
          <Show when={action.icon}>
            {(icon) => (
              <Dynamic
                component={icon()}
                class="size-[1em] touch:min-h-0! touch:min-w-0!"
              />
            )}
          </Show>
          {action.label}
        </Button>
      )}
    </For>
  );
}

function ToastBodyWrapper(props: {
  mobile?: boolean;
  accentColor: string;
  children: JSX.Element;
}) {
  return (
    <Show
      when={props.mobile}
      fallback={
        <Surface
          highlightColor={props.accentColor}
          hideBorder
          class="relative w-[90vw] sm:w-md p-2 sm:p-3 rounded-xl glass bg-toast"
        >
          {props.children}
        </Surface>
      }
    >
      <div class="island relative w-full p-2 rounded-xl bg-toast">
        {props.children}
      </div>
    </Show>
  );
}

function ToastContent(props: {
  toastId: number;
  toastType?: ToastType;
  message?: string;
  subtext?: string;
  actions?: ToastAction[];
  persistent?: boolean;
  /** When provided, drives the auto-dismiss timer AND shows the progress bar. */
  duration?: number;
  embed?: Component;
  custom?: CustomToastConfig;
  /** Render the mobile variant (no highlight border, text-xs, simplified). */
  mobile?: boolean;
  /** Avoid entrance motion when this toast is replacing another toast. */
  skipOpenAnimation?: boolean;
  /** Called when this toast is removed from the DOM, so callers can clean up tracking. */
  onDismiss?: () => void;
}) {
  const styles = () => (props.toastType ? TOAST_STYLES[props.toastType] : null);

  const accentColor = () => {
    if (props.custom?.color) return props.custom.color;
    return styles()?.borderColor ?? 'var(--color-edge)';
  };

  // progress: 1 = full time remaining, 0 = expired.
  // Only meaningful (and only rendered) when props.duration is explicitly set.
  const [progress, setProgress] = createSignal(1);

  const showProgress = () => false;

  const [isHovered, setIsHovered] = createSignal(false);

  let elapsed = 0;

  onCleanup(() => props.onDismiss?.());

  onMount(() => {
    // Persistent toasts never auto-dismiss
    if (props.persistent) return;

    const duration = props.duration ?? 3000;
    let lastTime: number | null = null;
    let rafId: number;

    const update = () => {
      const currentTime = performance.now();

      if (lastTime === null) {
        lastTime = currentTime;
      }

      // Only accumulate time when not hovered
      if (!isHovered()) {
        elapsed += currentTime - lastTime;
      }
      lastTime = currentTime;

      const remaining = Math.max(0, 1 - elapsed / duration);
      setProgress(remaining);

      if (remaining > 0) {
        rafId = requestAnimationFrame(update);
      } else {
        toaster.dismiss(props.toastId);
      }
    };

    rafId = requestAnimationFrame(update);
    onCleanup(() => cancelAnimationFrame(rafId));
  });

  // Reset timer when user starts hovering
  createEffect(
    on(isHovered, (hovered) => {
      if (hovered && !props.persistent) {
        elapsed = 0;
        setProgress(1);
      }
    })
  );

  return (
    <Toast
      toastId={props.toastId}
      class={cn(
        `relative overflow-visible pointer-events-auto
        transition-[transform,opacity] duration-100 ease-in data-closed:opacity-0 data-[swipe=move]:translate-x-(--kb-toast-swipe-move-x)
        data-[swipe=move]:transition-none
        data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:ease-out data-[swipe=cancel]:duration-200
        data-[swipe=end]:data-[swipe-direction=right]:animate-swipe-out
        data-[swipe=end]:data-[swipe-direction=left]:animate-swipe-out-left`,
        !props.skipOpenAnimation && 'data-opened:animate-slide-in',
        props.mobile && 'w-full'
      )}
      persistent={true}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ToastBodyWrapper mobile={props.mobile} accentColor={accentColor()}>
        <Switch>
          {/* ── Embed layout ── */}
          <Match when={props.embed}>
            {(embed) => (
              <>
                <Dynamic component={embed()} />
                <Toast.CloseButton class="absolute top-2 right-2 z-user-highlight">
                  <Button variant="ghost" size="icon-sm">
                    <XIcon />
                  </Button>
                </Toast.CloseButton>
              </>
            )}
          </Match>

          {/* ── Custom layout ── */}
          <Match when={props.custom}>
            {(customConfig) => {
              // A persistent prompt on mobile can't borrow the transient
              // one-line treatment: with the body and close button stripped it
              // reduces to a bare title that never goes away. Give it the full
              // card — description, close button, and its actions on their own
              // row so the tap targets aren't fighting a truncated title.
              const stacked = () => Boolean(props.mobile && props.persistent);
              const showContent = () =>
                Boolean(customConfig().content) && (!props.mobile || stacked());
              return (
                <>
                  <div class="flex items-center gap-2 justify-between">
                    <Show when={customConfig().icon && !props.mobile}>
                      {(_) => {
                        const icon = customConfig().icon!;
                        return (
                          <div class="size-5 flex shrink-0 justify-center items-center rounded-full p-0.75">
                            <Dynamic component={icon} />
                          </div>
                        );
                      }}
                    </Show>
                    <Toast.Title
                      class={cn(
                        'font-semibold grow shrink truncate text-left flex items-center',
                        props.mobile ? 'text-xs' : 'text-ink',
                        stacked() && 'text-sm'
                      )}
                    >
                      {customConfig().title}
                    </Toast.Title>
                    <Show when={customConfig().actions?.length && !stacked()}>
                      <ActionButtons
                        actions={customConfig().actions!}
                        mobile={props.mobile}
                      />
                    </Show>
                    <Show when={!props.mobile || props.persistent}>
                      <Toast.CloseButton>
                        <Button variant="ghost" size="icon-sm">
                          <XIcon />
                        </Button>
                      </Toast.CloseButton>
                    </Show>
                  </div>
                  <Show when={showContent()}>
                    <div
                      class={cn(
                        'my-2',
                        props.mobile && 'text-xs text-ink-muted'
                      )}
                    >
                      {customConfig().content?.()}
                    </div>
                  </Show>
                  <Show when={stacked() && customConfig().actions?.length}>
                    <div class="flex justify-end gap-2">
                      <ActionButtons actions={customConfig().actions!} mobile />
                    </div>
                  </Show>
                </>
              );
            }}
          </Match>

          {/* ── Standard layout ── */}
          <Match when={styles()}>
            {(s) => (
              <>
                <div class="flex items-center gap-2 justify-between">
                  <div
                    class="size-5 flex shrink-0 justify-center items-center rounded-full p-0.75"
                    style={{ 'background-color': s().borderColor }}
                  >
                    <Dynamic
                      component={s().icon}
                      class={cn(
                        'size-3.5 text-surface',
                        props.toastType === ToastType.LOADING
                          ? 'animate-spin'
                          : ''
                      )}
                    />
                  </div>
                  <Toast.Title
                    class={cn(
                      'font-semibold grow shrink truncate text-left',
                      props.mobile ? 'text-xs' : 'text-ink'
                    )}
                  >
                    {props.message}
                  </Toast.Title>
                  <Show when={props.actions?.length}>
                    <ActionButtons
                      actions={props.actions!}
                      mobile={props.mobile}
                    />
                  </Show>
                  <Show when={!props.mobile}>
                    <Toast.CloseButton>
                      <Button variant="ghost" size="icon-sm">
                        <XIcon />
                      </Button>
                    </Toast.CloseButton>
                  </Show>
                </div>
                <Show when={props.subtext && !props.mobile}>
                  <Toast.Description class="text-sm text-ink-extra-muted ml-7">
                    {props.subtext}
                  </Toast.Description>
                </Show>
              </>
            )}
          </Match>
        </Switch>

        {/* Progress bar — only rendered when an explicit duration was passed */}
        <Show when={showProgress()}>
          <div
            class="absolute bottom-0 h-1 left-0"
            style={{
              'background-color': accentColor(),
              width: `${(1 - progress()) * 100}%`,
            }}
          />
        </Show>
      </ToastBodyWrapper>
    </Toast>
  );
}

// ─── promise helper ──────────────────────────────────────────────────────────

async function promise<T>(
  promiseArg: Promise<T>,
  options: {
    loading: string;
    success?: string | ((result: T) => string);
    error?: string | ((error: any) => string);
    toastTypeDeterminer?: (result: T) => ToastType;
    subtext?: string;
    /** When true, don't render the loading/result toasts on mobile. */
    hideOnMobile?: boolean;
  }
): Promise<T> {
  if (isMobile() && options.hideOnMobile) return promiseArg;

  const useMobile = isMobile();
  const region = useMobile ? 'mobile-toast-region' : 'toast-region';
  const skipOpenAnimation = dismissActiveToast(region);

  const toastId = toaster.show(
    (props) => (
      <ToastContent
        toastId={props.toastId}
        toastType={ToastType.LOADING}
        message={options.loading}
        subtext={options.subtext}
        persistent={true}
        mobile={useMobile}
        skipOpenAnimation={skipOpenAnimation}
        onDismiss={() => clearTrackedToast(region, props.toastId)}
      />
    ),
    { region }
  );
  setActiveToastId(region, toastId);

  return promiseArg
    .then((result) => {
      toaster.dismiss(toastId);

      if (options.success) {
        const successMessage =
          typeof options.success === 'function'
            ? options.success(result)
            : options.success;

        const toastType =
          options.toastTypeDeterminer?.(result) ?? ToastType.SUCCESS;

        createToast(successMessage, toastType, {
          hideOnMobile: options.hideOnMobile,
        });
      }

      return result;
    })
    .catch((error) => {
      toaster.dismiss(toastId);
      if (options.error) {
        const errorMessage =
          typeof options.error === 'function'
            ? options.error(error)
            : options.error;
        failure(errorMessage, { hideOnMobile: options.hideOnMobile });
      }
      throw error;
    });
}

// ─── createToast (internal) ──────────────────────────────────────────────────

function createToast(
  message: string,
  toastType: ToastType,
  options?: ToastSuccessOptions
) {
  const { subtext, actions, duration, stack, hideOnMobile } = options ?? {};

  if (isMobile() && hideOnMobile) return undefined;

  if (!stack) {
    const key = createToastKey(message, toastType);
    const existingToast = recentToasts.get(key);
    if (existingToast?.timeoutId) {
      clearTimeout(existingToast.timeoutId);
    }
  }

  const useMobile = isMobile();
  const region = useMobile ? 'mobile-toast-region' : 'toast-region';
  const skipOpenAnimation = dismissActiveToast(region);

  const toastId = toaster.show(
    (props) => (
      <ToastContent
        toastId={props.toastId}
        toastType={toastType}
        message={message}
        subtext={subtext}
        actions={actions}
        // Pass duration only when explicitly provided — this is what gates the progress bar.
        // When undefined, ToastContent falls back to its own default dismiss timing internally.
        duration={duration}
        mobile={useMobile}
        skipOpenAnimation={skipOpenAnimation}
        onDismiss={() => {
          clearTrackedToast(region, props.toastId);
        }}
      />
    ),
    { region }
  );

  setActiveToastId(region, toastId);

  if (!stack) {
    const key = createToastKey(message, toastType);
    const timeoutId = setTimeout(() => {
      recentToasts.delete(key);
    }, THROTTLE_DURATION);
    recentToasts.set(key, {
      message,
      toastType,
      timestamp: Date.now(),
      timeoutId,
      toastId,
      subtext,
      actions,
    });
  }

  return toastId;
}

// ─── embed ───────────────────────────────────────────────────────────────────

function embed(
  component: Component,
  options?: {
    persistent?: boolean;
    duration?: number;
    region?: string;
  }
) {
  const useMobile = isMobile();
  const region =
    options?.region ?? (useMobile ? 'mobile-toast-region' : 'toast-region');
  const skipOpenAnimation = replaceActiveToast(region, options?.persistent);
  const toastId = toaster.show(
    (props) => (
      <ToastContent
        toastId={props.toastId}
        embed={component}
        persistent={options?.persistent}
        duration={options?.duration}
        mobile={useMobile}
        skipOpenAnimation={skipOpenAnimation}
        onDismiss={() => clearTrackedToast(region, props.toastId)}
      />
    ),
    { region }
  );
  trackActiveToast(region, toastId, options?.persistent);
  return toastId;
}

// ─── custom ──────────────────────────────────────────────────────────────────

/**
 * Show a toast with a fully custom title, icon, accent color, body content,
 * and actions row — while still using the shared Surface chrome and
 * progress/dismiss machinery.
 */
function custom(
  config: CustomToastConfig,
  options?: {
    persistent?: boolean;
    duration?: number;
    region?: string;
    onDismiss?: () => void;
  }
): number {
  const useMobile = isMobile();
  const region =
    options?.region ?? (useMobile ? 'mobile-toast-region' : 'toast-region');
  const skipOpenAnimation = replaceActiveToast(region, options?.persistent);
  const toastId = toaster.show(
    (props) => (
      <ToastContent
        toastId={props.toastId}
        custom={config}
        persistent={options?.persistent}
        duration={options?.duration}
        mobile={useMobile}
        skipOpenAnimation={skipOpenAnimation}
        onDismiss={() => {
          clearTrackedToast(region, props.toastId);
          options?.onDismiss?.();
        }}
      />
    ),
    { region }
  );
  trackActiveToast(region, toastId, options?.persistent);
  return toastId;
}

// ─── upload helper (kept for backwards compat) ───────────────────────────────

export function createUploadToast(message: string) {
  return toaster.show(
    (props) => (
      <ToastContent
        toastId={props.toastId}
        toastType={ToastType.LOADING}
        message={message}
        persistent={true}
      />
    ),
    { region: 'stable-toast' }
  );
}

// ─── public API ──────────────────────────────────────────────────────────────

export const toast = {
  success,
  failure,
  alert,
  promise,
  embed,
  custom,
  dismiss,
};
